import { Pencil } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";

import { useGoal } from "../api";
import { formatPct } from "../constants";
import { GoalAnalysisPanel } from "./GoalAnalysisPanel";
import { LinkedObjectivesList } from "./LinkedObjectivesList";

/** Body of the goal detail drawer: progress roll-up, description, success
 *  criteria, and the linked objectives. Write users also get Edit + the
 *  (Phase 4) AI insights entry point. */
export function GoalDetail({
  goalId,
  canWrite,
  openwebuiUrl,
  onEdit,
}: {
  goalId: string;
  canWrite: boolean;
  openwebuiUrl?: string | null;
  onEdit: () => void;
}) {
  const { data: goal, isLoading, error } = useGoal(goalId);

  if (isLoading) {
    return <p className="p-5 text-sm text-muted-foreground">Loading goal…</p>;
  }
  if (error || !goal) {
    return <p className="p-5 text-sm text-danger">Couldn't load this goal. Try again.</p>;
  }

  const objectiveWord = goal.objective_count === 1 ? "objective" : "objectives";

  return (
    <div className="flex flex-col gap-6 p-5">
      <section className="rounded-lg border border-border bg-surface-raised/50 p-4">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Progress
          </span>
          <span className="text-sm font-bold tabular-nums text-foreground">
            {formatPct(goal.progress_pct)}
          </span>
        </div>
        <Progress value={goal.progress_pct} className="mt-2" />
        <p className="mt-2 text-xs text-muted-foreground">
          Rolled up from {goal.objective_count} linked {objectiveWord} (equal-weighted).
        </p>
      </section>

      {goal.description ? (
        <section>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            About
          </h3>
          <p className="mt-1.5 text-sm leading-relaxed text-foreground">{goal.description}</p>
        </section>
      ) : null}

      {goal.success_criteria ? (
        <section>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Success criteria
          </h3>
          <p className="mt-1.5 whitespace-pre-line text-sm leading-relaxed text-foreground">
            {goal.success_criteria}
          </p>
        </section>
      ) : null}

      <section>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Linked objectives ({goal.objective_count})
        </h3>
        <div className="mt-2">
          <LinkedObjectivesList objectives={goal.objectives} />
        </div>
      </section>

      {canWrite ? (
        <>
          <section className="flex flex-wrap gap-2 border-t border-border pt-4">
            <Button variant="outline" size="sm" onClick={onEdit}>
              <Pencil className="mr-1.5 h-3.5 w-3.5" />
              Edit goal
            </Button>
          </section>
          {/* Write-only: background local-LLM analysis (progress reality-check,
              clarified success criteria, recommendations). Live Snowflake
              exploration lives in the Open WebUI tool — see Phase 4c. */}
          <GoalAnalysisPanel
            goalId={goalId}
            goalTitle={goal.title}
            openwebuiUrl={openwebuiUrl}
          />
        </>
      ) : null}
    </div>
  );
}
